import type { IHttpClient } from '@wix/yoshi-flow-editor';
import type { FedopsLogger } from '../utils/monitoring/FedopsLogger';
import type {
  MenuOrderingSettings,
  TimeSlot,
} from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import { OrdersSettingsClient } from 'root/api/ordersSettingsClient';
import type { ReportError } from 'root/api/utils/getMonitoredApiCall';
import type {
  DispatchInfo,
  MenusAvailabilityStatus,
  TimeSlotsPerMenu,
} from 'root/types/businessTypes';
import {
  convertMenuAvailabilityStatusArrayToKeyValue,
  convertTimeSlotsPerMenuArrayToKeyValue,
} from 'root/api/utils/utils';
import type { Address } from '@wix/ambassador-restaurants-operations-v1-operation/types';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import type { PanoramaErrorMonitor } from '@wix/panorama-client/dist/types/panorama-error-monitor';
import type { ErrorMonitorUserData } from '@wix/panorama-client/dist/types/types';
import type { IBIReporterService } from './biReporterService';

export type OnErrorType = (error: Error) => void;
export type FetchMenusAvailabilityStatusByDispatchInfoParams = {
  operationId?: string;
  dispatchInfo: DispatchInfo;
  onError?: OnErrorType;
};
export type ListMenusAvailabilityStatusByTimeslotParams = {
  operationId: string;
  timeSlot: TimeSlot;
  onError?: OnErrorType;
};
export type GetMenuOrderingSettingsOperationIdParams = {
  operationId: string | undefined;
  onError?: OnErrorType;
};
export type ListFirstAvailableTimeSlotsForMenusParams = {
  operationId?: string;
  deliveryAddress?: Address;
  onError?: (error: Error) => void;
};

export interface IOrdersSettingsService {
  getMenuOrderingSettingsByOperationId: ({
    operationId,
    onError,
  }: GetMenuOrderingSettingsOperationIdParams) => Promise<MenuOrderingSettings[]>;
  listMenusAvailabilityStatusByTimeslot: ({
    operationId,
    timeSlot,
    onError,
  }: ListMenusAvailabilityStatusByTimeslotParams) => Promise<MenusAvailabilityStatus>;
  fetchMenusAvailabilityStatusByDispatchInfo: ({
    operationId,
    dispatchInfo,
    onError,
  }: FetchMenusAvailabilityStatusByDispatchInfoParams) => Promise<MenusAvailabilityStatus>;
  fetchFirstAvailableTimeSlotsForMenus: ({
    deliveryAddress,
    operationId,
    onError,
  }: ListFirstAvailableTimeSlotsForMenusParams) => Promise<TimeSlotsPerMenu>;
}
export interface getMenuOrderingSettingsOperationIdParams {}

export const OrdersSettingsService = ({
  httpClient,
  errorHandler,
  fedopsLogger,
  biReporterService,
  errorMonitor,
  reportError,
  useCalculateFirstAvailableAPI,
}: {
  httpClient: IHttpClient;
  errorHandler: ErrorHandlerPublicAPI;
  fedopsLogger: FedopsLogger;
  biReporterService?: IBIReporterService;
  errorMonitor?: PanoramaErrorMonitor<ErrorMonitorUserData>;
  reportError?: ReportError;
  useCalculateFirstAvailableAPI?: boolean;
}): IOrdersSettingsService => {
  const ordersSettingsClient = new OrdersSettingsClient(
    httpClient,
    errorHandler,
    errorMonitor,
    fedopsLogger,
    reportError,
    biReporterService,
    useCalculateFirstAvailableAPI
  );

  const ordersSettingsServiceObject = {
    getMenuOrderingSettingsByOperationId: async ({
      operationId,
      onError,
    }: GetMenuOrderingSettingsOperationIdParams) => {
      const response = await ordersSettingsClient.getMenuOrderingSettingsByOperationId(
        operationId,
        onError
      );
      return response.menuOrderingSettings || [];
    },
    listMenusAvailabilityStatusByTimeslot: async ({
      operationId,
      timeSlot,
      onError,
    }: ListMenusAvailabilityStatusByTimeslotParams) => {
      const response = await ordersSettingsClient.listMenusAvailabilityStatusByTimeslot(
        operationId,
        timeSlot,
        onError
      );
      return convertMenuAvailabilityStatusArrayToKeyValue(response.menusAvailabilityStatus) || {};
    },
    fetchMenusAvailabilityStatusByDispatchInfo: async ({
      operationId,
      dispatchInfo,
      onError,
    }: FetchMenusAvailabilityStatusByDispatchInfoParams) => {
      const { startTime, endTime } = dispatchInfo?.selectedTimeSlot || {};
      if (operationId && startTime && endTime) {
        const timeSlot = {
          startTime: startTime?.toJSDate(),
          endTime: endTime?.toJSDate(),
        };
        return ordersSettingsServiceObject.listMenusAvailabilityStatusByTimeslot({
          operationId,
          timeSlot,
          onError,
        });
      } else {
        return {};
      }
    },
    fetchFirstAvailableTimeSlotsForMenus: async ({
      deliveryAddress,
      operationId,
      onError,
    }: ListFirstAvailableTimeSlotsForMenusParams) => {
      const response = await ordersSettingsClient.listFirstAvailableTimeSlotsForMenus({
        operationId,
        deliveryAddress,
        onError,
      });
      return convertTimeSlotsPerMenuArrayToKeyValue(response.timeSlotsPerMenu) || {};
    },
  };

  return ordersSettingsServiceObject;
};
